import {baseApi} from './base'
import {setAuthHeaders} from './helpers'
import type {EventInterface} from '@interfaces/usage/event'
import type {UsageHomeSummaryInterface} from '@interfaces/usage/homeSummary'
import type {MemberActivityInterface} from '@interfaces/usage/memberActivity'
import type {
  OrganizationUsageSummaryInterface,
  UsagePeriodDays,
} from '@interfaces/usage/organizationSummary'

const usageAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsageHomeSummary: builder.query<UsageHomeSummaryInterface, void>({
      query: () => ({
        url: 'usage/home-summary/',
      }),
      extraOptions: {refetchOnMountOrArgChange: true},
      providesTags: ['UsageHomeSummary'],
    }),
    // Account management → Users: activity bucket per member (AB#6636).
    getMemberActivity: builder.query<MemberActivityInterface, void>({
      query: () => ({
        url: 'usage/member-activity/',
      }),
    }),
    // Account management → Usage: organization-level insights (AB#6637).
    getOrganizationUsageSummary: builder.query<OrganizationUsageSummaryInterface, UsagePeriodDays>({
      query: (periodDays) => ({
        url: 'usage/organization-summary/',
        params: {period_days: periodDays},
      }),
    }),
    usageEvent: builder.mutation<{message: string}, EventInterface>({
      query: (event) => ({
        url: '/usage/event/',
        method: 'POST',
        body: event,
      }),
      invalidatesTags: ['UsageHomeSummary'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetUsageHomeSummaryQuery,
  useGetMemberActivityQuery,
  useGetOrganizationUsageSummaryQuery,
  useUsageEventMutation,
} = usageAPI

/**
 * Sends a usage event while the page is being unloaded (tab close, reload, leaving
 * the app). An RTK mutation would start its fetch only after the page is gone;
 * `fetch(..., {keepalive: true})` is dispatched synchronously and outlives the page.
 * Best effort: a browser that refuses the keepalive request just loses this one event.
 */
export const sendUsageEventOnUnload = (event: EventInterface): void => {
  const headers = setAuthHeaders(new Headers({'Content-Type': 'application/json'}))
  fetch(`${import.meta.env.VITE_APP_API_BASE_URL || ''}/core/api/usage/event/`, {
    method: 'POST',
    headers,
    body: JSON.stringify(event),
    keepalive: true,
  }).catch(() => undefined)
}
